import { useEffect, useState } from 'react';
import { View, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { Appbar, Card, Text, TextInput, Button, Chip, useTheme, HelperText, Surface, Switch, ActivityIndicator, Dialog, Portal } from 'react-native-paper';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import Logo from '../../../src/components/Logo';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import { VehicleAssignmentModal } from '../../../src/components/VehicleAssignmentModal';
import { TransferModal } from '../../../src/components/TransferModal';
import { CustomDatePicker } from '../../../src/components/CustomDatePicker';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { updateDriver, transferDriver, getOwnerDriverWeeklyDrivingReportPDF } from '../../../src/api/apiService';
import type { UpdateDriverPayload, FundingDisplay } from '../../../src/api/apiService';
import { API_ENDPOINTS } from '../../../src/api/config';
import apiService from '../../../src/api/apiService';
import { useTranslation } from 'react-i18next';
import { useCompanyContext } from '../../../src/context/CompanyContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  DriverFundingSummary,
  DriverLicenseIssueNotice,
  useLicenseIssueCopy,
} from '../../../src/components/DriverFundingSummary';

export default function DriverDetailScreen() {
  const { id, include_hidden: includeHiddenParam } = useLocalSearchParams<{ id: string; include_hidden?: string }>();
  const includeHidden = includeHiddenParam === 'true';
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation('manageDrivers');
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isLargeScreen = width > 1024;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { handleLogout, userData } = useUser();
  const { hasOwner, hasDriver, selectedProfile, updateProfile } = useProfile(userData?.groups || []);
  const [_loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [status, setStatus] = useState<'active' | 'inactive' | 'pending'>('active');
  const [relationshipStatus, setRelationshipStatus] = useState<string | null>(null);
  const [funding, setFunding] = useState<FundingDisplay | null>(null);
  const [licensePayer, setLicensePayer] = useState<string | null>(null);
  const isSelfPay = licensePayer === 'self' || funding?.payer === 'self';
  const [reportHidden, setReportHidden] = useState(false);
  const [togglingHidden, setTogglingHidden] = useState(false);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasLicenseError, setHasLicenseError] = useState(false);
  const [licenseErrorModalVisible, setLicenseErrorModalVisible] = useState(false);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const getCurrentWeekStart = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    return monday.toISOString().slice(0, 10);
  };
  const [reportWeekStart, setReportWeekStart] = useState(getCurrentWeekStart());
  const { selectedCompanyId } = useCompanyContext();

  const getReportFileName = () => `driver_${id}_weekly_driving_report_${reportWeekStart || 'current'}.pdf`;

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result?.toString().split(',')[1] || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

  const load = async () => {
    setLoading(true);
    try {
      const empResp: any = await apiService.get(
        `${API_ENDPOINTS.COMPANY_DRIVERS}${id}/${includeHidden ? '?include_hidden=true' : ''}`
      );
      const emp: any = empResp.data;
      setName(emp.user_full_name || '');
      setEmail(emp.user_email || '');
      setPhoneNumber(emp.user_phone_number || '');
      setAddress(emp.user_address || '');
      setLicenseNumber(emp.user_license_number || '');
      setEmailVerified(emp.user_email_verified || false);
      setStatus(emp.is_active ? 'active' : 'inactive');
      setRelationshipStatus(emp.relationship_status || null);
      setFunding(emp.funding || null);
      setLicensePayer(emp.license_payer || null);
      setReportHidden(Boolean(emp.report_hidden));
      setVehicles(emp.vehicles || []);
    } catch (e: any) {
      setError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id, includeHidden]);

  const toggleStatus = async (newValue: boolean) => {
    const newStatus = newValue ? 'active' : 'inactive';
    const previousStatus = status; // Store previous status for revert
    
    // Optimistically update UI immediately - use direct setState for immediate update
    setStatus(newStatus);
    setToggling(true);
    setError(null);
    
    try {
      const payload: UpdateDriverPayload = {
        is_active: newStatus === 'active',
      };
      
      const response = await updateDriver(Number(id), payload);
      if (response.ok) {
        // Verify the status from response matches what we set
        const responseData = response.data as any;
        if (responseData?.is_active !== undefined) {
          setStatus(responseData.is_active ? 'active' : 'inactive');
        }
        // The switch and relationship badge represent different server fields.
        // Refresh both after PATCH so an activated driver does not stay visually pending.
        if (responseData?.relationship_status !== undefined) {
          setRelationshipStatus(responseData.relationship_status);
        }
        if (responseData?.funding !== undefined) {
          setFunding(responseData.funding);
        }
      } else {
        // Revert on error
        setStatus(previousStatus);
        const errorData = response.data as any;
        // Check for license-related errors (check both error_code and license_error flag)
        const isLicenseError = errorData?.license_error === true || 
                               errorData?.error === 'license_required' ||
                               errorData?.error?.includes('license') || 
                               errorData?.detail?.includes('license') || 
                               errorData?.error?.includes('purchase');
        setHasLicenseError(isLicenseError);
        
        if (isLicenseError) {
          setError(t('bulkActions.licenseError', 'You need to purchase licenses to activate drivers.'));
          setLicenseErrorModalVisible(true);
        } else {
          setError(errorData?.detail || errorData?.error || t('driverDetails.errorUpdatingStatus', 'Error updating driver status'));
        }
      }
    } catch (e: any) {
      // Revert on error
      setStatus(previousStatus);
      // Check for license-related errors
      const errorData = e?.response?.data || {};
      const isLicenseError = errorData?.license_error === true ||
                             errorData?.error === 'license_required' ||
                             e?.message?.includes('license') || 
                             e?.message?.includes('purchase');
      setHasLicenseError(isLicenseError);
      
      if (isLicenseError) {
        setError(t('bulkActions.licenseError', 'You need to purchase licenses to activate drivers.'));
        setLicenseErrorModalVisible(true);
      } else {
        setError(e?.message || errorData?.detail || t('driverDetails.errorUpdatingStatus', 'Error updating driver status'));
      }
    } finally {
      setToggling(false);
    }
  };

  const toggleReportHidden = async () => {
    const previous = reportHidden;
    const next = !previous;
    setReportHidden(next);
    setTogglingHidden(true);
    setError(null);
    try {
      const response = await updateDriver(Number(id), { report_hidden: next });
      if (!response.ok) {
        setReportHidden(previous);
        setError((response.data as any)?.detail || t('driverDetails.reportHiddenError', 'Error updating report visibility'));
        return;
      }
      setReportHidden(Boolean((response.data as any)?.report_hidden ?? next));
    } catch (e: any) {
      setReportHidden(previous);
      setError(e?.message || t('driverDetails.reportHiddenError', 'Error updating report visibility'));
    } finally {
      setTogglingHidden(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: UpdateDriverPayload = {
        full_name: name,
        email: email,
        phone_number: phoneNumber || undefined,
        address: address || undefined,
        license_number: licenseNumber || undefined,
        // Self-pay is read-only here. Editing profile fields must not send an
        // inactive employment bit (which can terminate a direct association).
        ...(isSelfPay ? {} : { is_active: status === 'active' }),
      };
      
      const response = await updateDriver(Number(id), payload);
      if (response.ok) {
        router.back();
      } else {
        setError((response.data as any)?.detail || (response.data as any)?.email?.[0] || 'Error updating driver');
      }
    } catch (e: any) {
      setError(e?.message || 'Error updating driver');
    } finally {
      setSaving(false);
    }
  };

  const isAssigned = (vehicleId: number) => vehicles.some((v: any) => v.id === vehicleId);

  const toggleAssign = async (vehicleId: number) => {
    try {
      if (isAssigned(vehicleId)) {
        // Unassign (delete relation)
        await apiService.post(`${API_ENDPOINTS.VEHICLES}${vehicleId}/unassign/`, { employment_id: Number(id) });
        setVehicles(v => v.filter((x: any) => x.id !== vehicleId));
      } else {
        await apiService.post(`${API_ENDPOINTS.VEHICLES}${vehicleId}/assign/`, { employment_id: Number(id) });
        await load();
      }
    } catch (e: any) {
      setError(e?.message || 'Error');
    }
  };

  const handleBulkAssign = async (vehicleIds: number[]) => {
    try {
      // Assign each vehicle sequentially
      for (const vehicleId of vehicleIds) {
        await apiService.post(`${API_ENDPOINTS.VEHICLES}${vehicleId}/assign/`, { employment_id: Number(id) });
      }
      // Refresh data to ensure consistency
      await load();
    } catch (e: any) {
      setError(e?.message || 'Error assigning vehicles');
      throw e; // Re-throw to let modal handle it
    }
  };

  const handleTransfer = async (targetCompanyId: number) => {
    const response = await transferDriver({
      employment_id: Number(id),
      target_company_id: targetCompanyId,
    });
    
    if (!response.ok) {
      const errorData = response.data as any;
      const isLicenseError = errorData?.license_error || errorData?.error === 'license_required' || errorData?.detail?.includes('license');
      if (isLicenseError) {
        throw new Error(t('transfer.licenseError', 'Target company has no available driver licenses.'));
      }
      throw new Error(errorData?.error || errorData?.detail || 'Error transferring driver');
    }
    
    // Refresh data and navigate back
    await load();
    router.back();
  };

  const handleExportWeeklyReport = async () => {
    if (!id) return;

    setExportingReport(true);
    setExportError(null);
    try {
      const response = await getOwnerDriverWeeklyDrivingReportPDF(
        id,
        reportWeekStart || undefined,
        reportHidden,
      );
      if (!response.ok) {
        throw new Error(t('driverDetails.exportError', 'Error exporting weekly hours report.'));
      }
      const fileName = getReportFileName();
      if (Platform.OS === 'web') {
        const blobUrl = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
        return;
      }

      const sharingAvailable = await Sharing.isAvailableAsync();
      if (!sharingAvailable) {
        throw new Error(t('driverDetails.exportShareUnavailable', 'Sharing is not available on this device.'));
      }

      const fileUri = `${FileSystem.documentDirectory}${fileName}`;
      const base64 = await blobToBase64(response.data);
      await FileSystem.writeAsStringAsync(fileUri, base64, { encoding: FileSystem.EncodingType.Base64 });
      await Sharing.shareAsync(fileUri, {
        mimeType: 'application/pdf',
        dialogTitle: t('driverDetails.shareReportTitle', 'Share weekly hours report'),
        UTI: 'com.adobe.pdf',
      });
    } catch (e: any) {
      setExportError(e?.message || t('driverDetails.exportError', 'Error exporting weekly hours report.'));
    } finally {
      setExportingReport(false);
    }
  };

  // Calcular height total do header incluindo status bar
  const headerHeight = 48; // Altura compacta para mobile
  const topPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
  const totalHeaderHeight = isLargeScreen ? 0 : headerHeight + topPadding;
  const relationshipIsCurrent = relationshipStatus === 'active' || relationshipStatus === 'associated';
  const relationshipLabel = relationshipStatus === 'associated'
    ? t('funding.relationshipStates.associated', 'Associated')
    : relationshipStatus === 'active'
      ? t('funding.relationshipStates.active', 'Active')
      : relationshipStatus === 'pending'
        ? t('funding.relationshipStates.pending', 'Pending')
        : relationshipStatus === 'deactivated'
          ? t('funding.relationshipStates.deactivated', 'Deactivated')
          : t('funding.relationshipStates.unknown', 'Unknown');

  const { label: licenseIssueLabel } = useLicenseIssueCopy(funding, relationshipStatus, name);
  const coverageStatusLabel = licenseIssueLabel ?? (funding?.coverage_status === 'active'
    ? t('funding.coverageStates.active', 'Active')
    : funding?.coverage_status === 'awaiting'
      ? t('funding.coverageStates.awaiting', 'Awaiting coverage')
      : funding?.coverage_status === 'not_covered'
        ? t('funding.coverageStates.not_covered', 'No active coverage')
        : t('funding.coverageStates.unknown', 'Unknown'));
  const renderHeader = () => {
    if (isLargeScreen) return null;
    return (
      <Appbar.Header style={{ 
        height: headerHeight + topPadding, 
        backgroundColor: theme.colors.surface, 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1,
        alignItems: 'center',
        paddingTop: topPadding,
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      {isLargeScreen && (
        <Sidebar isOpen={true} onClose={() => {}} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
      )}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(isLargeScreen ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
      <Appbar.Header style={isLargeScreen ? {} : { display: 'none' }}>
        <Appbar.BackAction onPress={() => router.back()} />
        <Appbar.Content title={t('driverDetailsTitle', 'Driver Details')} />
        <Appbar.Action icon="swap-horizontal" onPress={() => setTransferModalVisible(true)} />
        <Appbar.Action icon="content-save" onPress={save} disabled={saving} />
      </Appbar.Header>
      <ScrollView style={{ flex: 1, padding: 12, ...(isLargeScreen ? {} : { marginTop: totalHeaderHeight }) }}>
        <Card style={{ marginBottom: 12 }}>
          <Card.Title title={t('driverDetails.personalInfo', 'Personal Information')} />
          <Card.Content>
            <TextInput 
              label={t('name','Name')} 
              value={name} 
              onChangeText={setName} 
              style={{ marginBottom: 8 }}
              mode="outlined"
            />
            <View style={{ marginBottom: 8 }}>
              <TextInput 
                label={t('email','Email')} 
                value={email} 
                onChangeText={setEmail} 
                autoCapitalize="none" 
                keyboardType="email-address" 
                mode="outlined"
                disabled={emailVerified}
                right={
                  emailVerified ? (
                    <TextInput.Icon icon="check-circle" />
                  ) : (
                    <TextInput.Icon icon="alert-circle" />
                  )
                }
              />
              {emailVerified && (
                <HelperText type="info" style={{ marginTop: 4 }}>
                  {t('driverDetails.emailVerified', 'Email is verified. Cannot be changed.')}
                </HelperText>
              )}
              {!emailVerified && email && (
                <HelperText type="info" style={{ marginTop: 4 }}>
                  {t('driverDetails.emailUnverified', 'Email is not verified. You can change it, but verification will be required.')}
                </HelperText>
              )}
            </View>
            <TextInput 
              label={t('driverDetails.phoneNumber', 'Phone Number')} 
              value={phoneNumber} 
              onChangeText={setPhoneNumber} 
              keyboardType="phone-pad" 
              style={{ marginBottom: 8 }}
              mode="outlined"
            />
            <TextInput 
              label={t('driverDetails.address', 'Address')} 
              value={address} 
              onChangeText={setAddress} 
              style={{ marginBottom: 8 }}
              mode="outlined"
              multiline
            />
            <TextInput 
              label={t('driverDetails.licenseNumber', 'License Number')} 
              value={licenseNumber} 
              onChangeText={setLicenseNumber} 
              style={{ marginBottom: 8 }}
              mode="outlined"
            />
            {error && <HelperText type="error">{error}</HelperText>}
          </Card.Content>
        </Card>

        <Card style={{ marginBottom: 12 }}>
          <Card.Title title={t('driverDetails.employmentInfo', 'Employment Information')} />
          <Card.Content>
            {/* The relationship control already displays status. Hide financing
                detail while company activation is pending; it is verbose and can
                show available coverage beside an inactive relationship. Active
                and deactivated relationships retain distinct payer/coverage. */}
            {isSelfPay || relationshipStatus === 'pending' ? null : funding?.payer === 'company' || (funding?.payer === 'uncovered'
              && funding.coverage_status === 'not_covered'
              && relationshipStatus === 'deactivated') ? (
              <View style={{ marginBottom: 12 }}>
                <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                  {t('funding.payerLabel', 'Payer')}: {funding?.payer === 'company'
                    ? t('funding.payers.company', 'Company')
                    : t('funding.payers.uncovered', 'Uncovered')}
                </Text>
                <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                  {funding?.payer === 'company'
                    ? t('funding.companyCoverageLabel', 'Company coverage')
                    : t('funding.coverageLabel', 'Coverage')}: {coverageStatusLabel}
                </Text>
              </View>
            ) : (
              <DriverFundingSummary
                funding={funding}
                relationshipStatus={relationshipStatus}
                driverName={name}
                prominent
                testID="driver-funding-summary"
              />
            )}
            <Surface
              elevation={1}
              style={{
                borderRadius: 12,
                padding: 16,
                backgroundColor: relationshipIsCurrent
                  ? theme.colors.primaryContainer 
                  : theme.colors.errorContainer,
                borderWidth: 1,
                borderColor: relationshipIsCurrent
                  ? theme.colors.primary
                  : theme.colors.error,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
                  {toggling ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <View
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        backgroundColor: relationshipIsCurrent
                          ? theme.colors.primary
                          : theme.colors.error,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text
                        style={{
                          color: relationshipIsCurrent
                            ? theme.colors.onPrimary
                            : theme.colors.onError,
                          fontSize: 20,
                        }}
                      >
                        {relationshipIsCurrent ? '✓' : '✗'}
                      </Text>
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <Text 
                      variant="titleMedium" 
                      style={{ 
                        fontWeight: '600',
                        color: relationshipIsCurrent
                          ? theme.colors.onPrimaryContainer
                          : theme.colors.onErrorContainer,
                        marginBottom: 4,
                      }}
                    >
                      {t('driverDetails.relationshipControlTitle', 'Company relationship')}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <View
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: relationshipIsCurrent
                            ? theme.colors.primary
                            : theme.colors.error,
                        }}
                      />
                      <Text
                        variant="bodyMedium"
                        style={{
                          color: relationshipIsCurrent
                            ? theme.colors.onPrimaryContainer
                            : theme.colors.onErrorContainer,
                          fontWeight: '500',
                        }}
                      >
                        {relationshipLabel}
                      </Text>
                    </View>
                  </View>
                </View>
                {!isSelfPay && (
                  <View style={{ alignItems: 'flex-end', marginLeft: 12 }}>
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant, marginBottom: 4 }}>
                      {t('driverDetails.relationshipActivationLabel', 'Activation by company')}
                    </Text>
                    <Switch
                      value={status === 'active'}
                      onValueChange={toggleStatus}
                      disabled={toggling}
                    />
                  </View>
                )}
              </View>
              {isSelfPay && (
                <View style={{ marginTop: 12 }}>
                  <Text variant="bodyMedium" style={{ color: relationshipIsCurrent ? theme.colors.onPrimaryContainer : theme.colors.onErrorContainer }}>
                    {t('driverDetails.selfPaySummary', 'Driver pays · No company seat')}
                  </Text>
                  <Text variant="bodySmall" style={{ color: relationshipIsCurrent ? theme.colors.onPrimaryContainer : theme.colors.onErrorContainer }}>
                    {t('funding.personalCoverageLabel', 'Personal coverage')}: {coverageStatusLabel}
                  </Text>
                  <DriverLicenseIssueNotice
                    funding={funding}
                    relationshipStatus={relationshipStatus}
                    driverName={name}
                    testID="driver-license-issue"
                  />
                </View>
              )}
              {toggling && (
                <HelperText type="info" style={{ marginTop: 8, marginLeft: 0 }}>
                  {t('driverDetails.updatingStatus', 'Updating status...')}
                </HelperText>
              )}
              {error && !hasLicenseError && (
                <HelperText type="error" style={{ marginTop: 8, marginLeft: 0 }}>
                  {error}
                </HelperText>
              )}
            </Surface>
          </Card.Content>
        </Card>

        <Card style={{ marginBottom: 12 }}>
          <Card.Title title={t('driverDetails.reportExportTitle', 'Weekly hours report')} />
          <Card.Content>
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
              {t('driverDetails.reportExportDescription', 'Export the selected driver weekly hours report as a PDF. The week start date defaults to the current Monday.')}
            </Text>
            {funding?.payer === 'self' && (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <View style={{ flex: 1 }}>
                    <Text>{t('driverDetails.reportHidden', 'Hidden from company reports')}</Text>
                    <Text style={{ color: theme.colors.onSurfaceVariant, fontSize: 12 }}>
                      {t('driverDetails.reportHiddenDescription', 'This is reversible. Hidden drivers require explicit inclusion in exports.')}
                    </Text>
                  </View>
                  <Switch value={reportHidden} onValueChange={toggleReportHidden} disabled={togglingHidden} />
                </View>
                {reportHidden && (
                  <HelperText type="info">
                    {t('driverDetails.hiddenExportIncluded', 'This export will explicitly include the hidden driver.')}
                  </HelperText>
                )}
              </>
            )}
            <CustomDatePicker
              label={t('driverDetails.weekStart', 'Week start')}
              value={reportWeekStart}
              onChange={setReportWeekStart}
              disabled={exportingReport}
            />
            <Button
              mode="contained"
              icon="file-pdf-box"
              onPress={handleExportWeeklyReport}
              loading={exportingReport}
              disabled={exportingReport}
            >
              {t('driverDetails.exportHoursReportPdf', 'Export hours report (PDF)')}
            </Button>
            {exportError && <HelperText type="error">{exportError}</HelperText>}
          </Card.Content>
        </Card>

        <Card>
          <Card.Title 
            title={t('vehicles.assignedVehicles','Assigned vehicles')}
            right={() => (
              <Button
                mode="outlined"
                icon="plus"
                onPress={() => setAssignmentModalVisible(true)}
                compact
              >
                {t('vehicles.assign', 'Assign')}
              </Button>
            )}
          />
          <Card.Content>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {vehicles.map((v: any) => (
                <Chip
                  key={v.id}
                  icon="car"
                  mode="flat"
                  onClose={() => toggleAssign(v.id)}
                >
                  {v.registration_number}
                </Chip>
              ))}
              {vehicles.length === 0 && (
                <Text style={{ color: theme.colors.onSurfaceVariant, fontStyle: 'italic' }}>
                  {t('vehicles.noAssignedVehicles', 'No vehicles assigned')}
                </Text>
              )}
            </View>
          </Card.Content>
        </Card>

        <VehicleAssignmentModal
          visible={assignmentModalVisible}
          onDismiss={() => setAssignmentModalVisible(false)}
          onAssign={handleBulkAssign}
          assignedVehicleIds={vehicles.map(v => v.id)}
        />

        <TransferModal
          visible={transferModalVisible}
          onDismiss={() => setTransferModalVisible(false)}
          onTransfer={handleTransfer}
          title={t('transfer.driverTitle', 'Transfer Driver')}
          description={t('transfer.driverDescription', 'Select the company to transfer this driver to. All vehicle assignments will be removed.')}
          currentCompanyId={selectedCompanyId || undefined}
        />
      </ScrollView>
      {!isLargeScreen && (
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
      )}

      <Portal>
        <Dialog 
          visible={licenseErrorModalVisible} 
          onDismiss={() => {
            setLicenseErrorModalVisible(false);
            setError(null);
            setHasLicenseError(false);
          }}
          style={isLargeScreen ? { maxWidth: 500, alignSelf: 'center', width: '90%' } : undefined}
        >
          <Dialog.Title>{t('bulkActions.licenseErrorTitle', 'Licenses Required')}</Dialog.Title>
          <Dialog.Content>
            <Text style={{ marginBottom: 16 }}>
              {t('bulkActions.licenseError', 'You need to purchase licenses to activate drivers.')}
            </Text>
            <Text style={{ color: theme.colors.onSurfaceVariant }}>
              {t('bulkActions.licenseErrorDescription', 'To activate this driver, you need to purchase additional driver licenses. Click the button below to go to your licenses management page.')}
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => {
              setLicenseErrorModalVisible(false);
              setError(null);
              setHasLicenseError(false);
            }}>
              {t('common.ok', 'OK')}
            </Button>
            <Button 
              mode="contained"
              icon="license"
              onPress={() => {
                setLicenseErrorModalVisible(false);
                setError(null);
                setHasLicenseError(false);
                router.push('/dashboard/manage-licenses');
              }}
            >
              {t('bulkActions.goToLicenses', 'Go to Licenses')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
      </Surface>
    </View>
  );
}


